import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import config from 'config';

const ConnectivityContext = createContext<{ isOnline: boolean } | undefined>(
  undefined,
);

const pingUrl = `${config.hasura.graphqlUri}/healthz`;
const timeoutMs = 3000;

interface ConnectivityProviderProps {
  children: ReactNode;
}

export const ConnectivityProvider = ({
  children,
}: ConnectivityProviderProps) => {
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  const verifyConnectivity = useCallback(async (): Promise<boolean> => {
    if (!navigator.onLine) {
      setIsOnline(false);
      return false;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

    try {
      const response = await fetch(pingUrl, {
        cache: 'no-store',
        signal: controller.signal,
        mode: 'cors',
      });

      clearTimeout(timeoutId);
      const online = response.ok;
      setIsOnline(online);
      return online;
    } catch {
      clearTimeout(timeoutId);
      setIsOnline(false);
      return false;
    }
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      verifyConnectivity();
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        verifyConnectivity();
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    verifyConnectivity();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [verifyConnectivity]);

  return (
    <ConnectivityContext.Provider value={{ isOnline }}>
      {children}
    </ConnectivityContext.Provider>
  );
};

export const useConnectivity = (): { isOnline: boolean } => {
  const context = useContext(ConnectivityContext);
  if (context === undefined) {
    throw new Error('useConnectivity should be used with ConnectivityProvider');
  }

  return context;
};
